/**
 * issue #98 FTask1: ジョブ完了メール通知の ON/OFF + 送信先メアドを localStorage に永続化する util。
 *
 * - default OFF (key 未設定で false) ← autoSaveSettings (default ON) と違い、 通知は明示 opt-in。
 * - 'true' / 'false' 以外の値は破損として扱い default false で復帰。
 * - localStorage 不可 (private browsing 等) でも throw せず default 値で復帰。
 *
 * 永続化 key (CLAUDE.md 永続化命名規約準拠):
 *   - `omakase-unshi:notify-enabled` 通知 ON/OFF
 *   - `omakase-unshi:notify-email`   送信先メアド
 */
const ENABLED_KEY = 'omakase-unshi:notify-enabled';
const EMAIL_KEY = 'omakase-unshi:notify-email';

export function getNotifyEnabled(): boolean {
  try {
    const v = localStorage.getItem(ENABLED_KEY);
    if (v === 'true') return true;
    if (v === 'false') return false;
    return false; // 未設定 / 破損値は default OFF に fallback
  } catch {
    return false;
  }
}

export function setNotifyEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, String(enabled));
  } catch {
    // ignore (quota / private browsing)
  }
}

export function getNotifyEmail(): string {
  try {
    return localStorage.getItem(EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
}

export function setNotifyEmail(email: string): void {
  try {
    localStorage.setItem(EMAIL_KEY, email);
  } catch {
    // ignore (quota / private browsing)
  }
}

/**
 * issue #137: 完了メール通知の設定バリデーション (純粋関数)。
 *
 * 「通知 ON なのに宛先メアドが空欄」という不整合だけを検出する。
 * - enabled === true && email.trim() === '' → エラー文言
 * - それ以外 (通知 OFF / メアド入力あり) → null
 *
 * 書式の妥当性 (@ の有無等) はここでは検証しない。 空欄以外は投入を止めず、
 * 不正書式は送信側 (handleSubmit) が notifyEmail を積まない形で握る (issue #98 の既存挙動)。
 */
export function validateNotifySettings({
  enabled,
  email,
}: {
  enabled: boolean;
  email: string;
}): string | null {
  if (enabled && email.trim() === '') {
    return '通知先メールアドレスを入力してください';
  }
  return null;
}

/**
 * issue #98 / #148: 実際に送信する完了通知メールアドレスを解決する。
 *
 * 「自動保存 ON かつ 通知 ON かつ メアドが妥当書式」のときだけ宛先を返し、 それ以外は undefined
 * (FormData に積まれず通知しない)。 自動保存 OFF で通知も抑止するのは二重防御 (NotifyToggle disabled と対)。
 * 書式は簡易チェック (@ と TLD の存在) のみ。 handleSubmit / エディタ再生成の両経路で同一判定を共有する。
 */
export function resolveNotifyEmail(autoSaveEnabled: boolean): string | undefined {
  const email = getNotifyEmail();
  const shouldNotify =
    autoSaveEnabled && getNotifyEnabled() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  return shouldNotify ? email : undefined;
}
